import { inject, Injectable } from '@angular/core';
import {
  ChatReplyDraftSnapshot,
} from '@chat/interfaces/chat-reply-snapshot.interfaces';
import {
  cloneChatQuoteData,
  cloneChatReplyMessageSnapshot,
} from '@chat/utils/chat-reply-snapshot.utils';
import { ChatComposerDraftKey, ChatComposerDraftState } from '@services/chat-composer-draft.interfaces';
import { AuthService } from '@services/auth.service';
import { LocalStorageService } from '@services/local-storage.service';

type StoredChatComposerDraftState = {
  text: string;
  reply: Pick<ChatReplyDraftSnapshot, 'quote' | 'replyMessageSnapshot'> | null;
};

@Injectable({
  providedIn: 'root'
})
export class ChatComposerDraftPersistenceService {
  private readonly authService = inject(AuthService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly draftStorageKeyPrefix: string = 'chatComposerDraft-';

  public load(draftKey: ChatComposerDraftKey): ChatComposerDraftState | null {
    const draftStorageKey = this.getDraftStorageKey(draftKey);
    if (!draftStorageKey) {
      return null;
    }

    const rawDraft = this.localStorage.getItem(draftStorageKey, true);
    const draft = this.normalizeDraftState(rawDraft);
    if (rawDraft !== null && !draft) {
      this.localStorage.removeItem(draftStorageKey);
    }

    return draft;
  }

  public save(draftKey: ChatComposerDraftKey, draft: ChatComposerDraftState | null | undefined): void {
    const draftStorageKey = this.getDraftStorageKey(draftKey);
    if (!draftStorageKey) {
      return;
    }

    if (!draft) {
      this.localStorage.removeItem(draftStorageKey);
      return;
    }

    const normalizedDraft = this.normalizeDraftState(draft);
    if (!normalizedDraft || this.isEmptyDraft(normalizedDraft)) {
      this.localStorage.removeItem(draftStorageKey);
      return;
    }

    this.localStorage.setItem(draftStorageKey, this.serializeDraftState(normalizedDraft));
  }

  public clear(draftKey: ChatComposerDraftKey): void {
    const draftStorageKey = this.getDraftStorageKey(draftKey);
    if (!draftStorageKey) {
      return;
    }

    this.localStorage.removeItem(draftStorageKey);
  }

  public move(fromDraftKey: ChatComposerDraftKey, toDraftKey: ChatComposerDraftKey): void {
    const fromStorageKey = this.getDraftStorageKey(fromDraftKey);
    const toStorageKey = this.getDraftStorageKey(toDraftKey);
    if (!fromStorageKey || !toStorageKey || fromStorageKey === toStorageKey) return;

    const rawDraft = this.localStorage.getItem(fromStorageKey, true);
    const draft = this.normalizeDraftState(rawDraft);
    if (!draft) return;

    this.localStorage.setItem(toStorageKey, this.serializeDraftState(draft));
    this.localStorage.removeItem(fromStorageKey);
  }

  public moveIfTargetMissing(fromDraftKey: ChatComposerDraftKey, toDraftKey: ChatComposerDraftKey): void {
    const fromStorageKey = this.getDraftStorageKey(fromDraftKey);
    const toStorageKey = this.getDraftStorageKey(toDraftKey);
    if (!fromStorageKey || !toStorageKey || fromStorageKey === toStorageKey) return;

    const rawDraft = this.localStorage.getItem(fromStorageKey, true);
    const draft = this.normalizeDraftState(rawDraft);
    if (!draft) return;

    const targetDraft = this.normalizeDraftState(this.localStorage.getItem(toStorageKey, true));
    if (targetDraft && !this.isEmptyDraft(targetDraft)) {
      this.localStorage.removeItem(fromStorageKey);
      return;
    }

    this.localStorage.setItem(toStorageKey, this.serializeDraftState(draft));
    this.localStorage.removeItem(fromStorageKey);
  }

  public clearReplyTargetByMessageId(draftKey: ChatComposerDraftKey, messageId: string | number | null | undefined): void {
    const normalizedMessageId = this.normalizeMessageKey(messageId);
    if (!normalizedMessageId) return;

    const draft = this.load(draftKey);
    if (!draft?.reply || !this.replyDraftRepliesTo(draft.reply, normalizedMessageId)) {
      return;
    }

    this.save(draftKey, {
      text: draft.text,
      reply: null,
    });
  }

  private normalizeDraftStorageId(draftKey: ChatComposerDraftKey): string | null {
    if (typeof draftKey === 'number') {
      return Number.isInteger(draftKey) && draftKey > 0 ? String(draftKey) : null;
    }

    if (typeof draftKey === 'string') {
      const normalizedDraftKey = draftKey.trim();
      if (/^[a-z0-9:_-]+$/i.test(normalizedDraftKey)) {
        return normalizedDraftKey;
      }
    }

    return null;
  }

  private normalizeUserId(userId: number | null | undefined): number | null {
    if (!Number.isInteger(userId) || userId < 1) {
      return null;
    }

    return userId;
  }

  private getDraftStorageKey(draftKey: ChatComposerDraftKey): string | null {
    const normalizedDraftKey = this.normalizeDraftStorageId(draftKey);
    const normalizedUserId = this.normalizeUserId(this.authService.parsedToken?.userId);
    if (normalizedDraftKey === null || normalizedUserId === null) {
      return null;
    }

    return `${this.draftStorageKeyPrefix}${normalizedUserId}-${normalizedDraftKey}`;
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private normalizeDraftState(rawValue: unknown): ChatComposerDraftState | null {
    if (typeof rawValue !== 'object' || rawValue === null) {
      return null;
    }

    return {
      text: this.normalizeText(Reflect.get(rawValue, 'text')),
      reply: this.normalizeReplyDraftState(Reflect.get(rawValue, 'reply')),
    };
  }

  private serializeDraftState(draft: ChatComposerDraftState): StoredChatComposerDraftState {
    const normalizedReply = this.normalizeReplyDraftState(draft.reply);

    return {
      text: this.normalizeText(draft.text),
      reply: normalizedReply ? {
        quote: cloneChatQuoteData(normalizedReply.quote),
        replyMessageSnapshot: cloneChatReplyMessageSnapshot(normalizedReply.replyMessageSnapshot),
      } : null,
    };
  }

  private isEmptyDraft(draft: ChatComposerDraftState): boolean {
    return draft.text.length === 0 && !draft.reply;
  }

  private normalizeReplyDraftState(rawValue: unknown): ChatReplyDraftSnapshot | null {
    if (typeof rawValue !== 'object' || rawValue === null) {
      return null;
    }

    const replyMessageSnapshot = cloneChatReplyMessageSnapshot(Reflect.get(rawValue, 'replyMessageSnapshot'));
    const replyMessageId = this.normalizeMessageKey(Reflect.get(rawValue, 'replyMessageId'))
      ?? replyMessageSnapshot?.messageId
      ?? null;
    const replyMessageGuid = this.normalizeMessageKey(replyMessageSnapshot?.guid);
    if (!replyMessageSnapshot || (!replyMessageId && !replyMessageGuid)) {
      return null;
    }

    return {
      replyMessageId,
      quote: cloneChatQuoteData(Reflect.get(rawValue, 'quote')),
      replyMessageSnapshot,
    };
  }

  private replyDraftRepliesTo(replyDraft: ChatReplyDraftSnapshot, messageId: string): boolean {
    return this.normalizeMessageKey(replyDraft.replyMessageId) === messageId
      || this.normalizeMessageKey(replyDraft.replyMessageSnapshot?.messageId) === messageId;
  }

  private normalizeMessageKey(value: unknown): string | null {
    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized.length > 0 ? normalized : null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return null;
  }
}
